import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tab, Tabs } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import { FProgressBar } from '../../../../base';
import GoalsContainer from './GoalsContainer';
import { FORTA_WHITE, FORTA_BLUE, FORTA_ORANGE } from '../../../../../utils/colors';
import { GoalTypes } from './constants';
import { behaviorActions } from '../../../../../store';

import type { ITreatmentPlanGoal } from '../../../../../utils/types';
import type { SyntheticEvent } from 'react';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: any;
}

const tabStyle = (error?: boolean) => {
  return {
    color: FORTA_WHITE,
    borderRadius: '10px 10px 0 0',
    px: 3,
    py: 0,
    overflow: 'visible !important',
    '&.Mui-selected': {
      color: FORTA_WHITE,
    },

    '&:before': error
      ? {
          content: '"!"',
          width: 21,
          height: 21,
          backgroundColor: FORTA_ORANGE,
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
          color: FORTA_WHITE,
          fontWeight: 'bold',
          borderRadius: '50%',
          position: 'absolute',
          right: -5,
          top: -5,
          bottom: 0,
          textAlign: 'center',
          zIndex: 1,
        }
      : {},
  };
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      sx={{
        display: 'flex',
        flexGrow: 1,
        borderRadius: '0 0 10px 10px',
        p: 3,
        backgroundColor: FORTA_BLUE.light3,
      }}
    >
      {value === index && children}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function Goals() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { loading: behaviorLoading } = useSelector((state: any) => state.behaviors);
  const { skillAcquisitionGoals, caregiverGoals } = useSelector((state: any) => state.goals);
  const [tabColor, setTabColor] = useState<string>(FORTA_BLUE.primary);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [activeSkillAcquisitionGoals, setActiveSkillAcquisitionGoals] = useState<
    ITreatmentPlanGoal[]
  >([]);
  const [activeCaregiverGoals, setActiveCaregiverGoals] = useState<ITreatmentPlanGoal[]>([]);

  useEffect(() => {
    dispatch(behaviorActions.getList({ getAccessTokenSilently }));
  }, []);

  useEffect(() => {
    if (!treatmentPlan || !skillAcquisitionGoals) return;

    setActiveSkillAcquisitionGoals(
      skillAcquisitionGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [],
    );
  }, [skillAcquisitionGoals, treatmentPlan]);

  useEffect(() => {
    if (!treatmentPlan || !caregiverGoals) return;

    setActiveCaregiverGoals(
      caregiverGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [],
    );
  }, [caregiverGoals, treatmentPlan]);

  const handleTabChange = (event: SyntheticEvent, newTab: number) => {
    switch (newTab) {
      case 0:
        setTabColor(FORTA_BLUE.primary);
        break;
      case 1:
        setTabColor(FORTA_BLUE.light1);
        break;
      case 2:
        setTabColor(FORTA_BLUE.light1);
        break;
      default:
        break;
    }

    setSelectedTab(newTab);
  };

  if (behaviorLoading) return <FProgressBar />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 3,
      }}
    >
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{
          position: 'relative',
          height: 80,
          overflow: 'visible !important',
          '& .MuiTabs-scroller': {
            overflow: 'visible !important',
          },
          '&:after': {
            content: '""',
            width: '100%',
            height: 40,
            position: 'absolute',
            top: 47,
            backgroundColor: tabColor,
            borderRadius: '0 10px 10px 10px',
            boxShadow: `0px 4px 4px ${FORTA_BLUE.light2}`,
          },
        }}
      >
        <Tab
          label="Behavior Reduction Goals"
          {...a11yProps(0)}
          sx={{ backgroundColor: FORTA_BLUE.primary, ...tabStyle() }}
        />
        <Tab
          label="Skill Acquisition Goals"
          {...a11yProps(1)}
          sx={{
            backgroundColor: FORTA_BLUE.light1,
            ...tabStyle(activeSkillAcquisitionGoals.length === 0),
          }}
        />
        <Tab
          label="Parent/Caregiver Goals"
          {...a11yProps(2)}
          sx={{
            backgroundColor: FORTA_BLUE.light1,
            ...tabStyle(activeCaregiverGoals.length === 0),
          }}
        />
      </Tabs>

      <TabPanel value={selectedTab} index={0}>
        <GoalsContainer type={GoalTypes.BehaviorReduction} />
      </TabPanel>
      <TabPanel value={selectedTab} index={1}>
        <GoalsContainer type={GoalTypes.SkillAcquisition} />
      </TabPanel>
      <TabPanel value={selectedTab} index={2}>
        <GoalsContainer type={GoalTypes.Caregiver} />
      </TabPanel>
    </Box>
  );
}

export default Goals;
